import type { LoadingStates } from 'Store'
import { createAsyncThunkWithErrorHandling, getAxiosInstance } from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import {
  WEBSOCKET_CLOSED,
  WEBSOCKET_CONNECT,
  WEBSOCKET_DISCONNECT,
  WEBSOCKET_ERROR,
  WEBSOCKET_MESSAGE,
  WEBSOCKET_OPEN
} from '@giantmachines/redux-websocket'
import { createAction, createSlice } from '@reduxjs/toolkit'

const genericActions = actionGenerator('report')

export enum ReportType {
  WEBSHOP = 'webshop',
  AFFILIATE = 'affiliate',
  GROUP_TICKET = 'group_ticket'
}

export enum WebsocketConnectionStatus {
  IDLE = 'idle',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  AUTHENTICATING = 'authenticating',
  OPEN = 'open',
  ERROR = 'error',
  CONNECTION_ERROR = 'connection error',
  CLOSING = 'closing',
  CLOSED = 'closed'
}

export interface ReportState {
  webShopOrderReportLoading: LoadingStates
  affiliateOrderReportLoading: LoadingStates
  ticketSaleReportLoading: LoadingStates
  singleWebShopOrderReport: ReportResponse | null
  singleAffiliateOrderReport: ReportResponse | null
  singleTicketSaleReport: ReportResponse | null
  websocketConnection: WebsocketConnectionStatus
  disconnectByError: boolean
  webShopReportGenerationTask: ReportGenerationTaskShape | null
  webShopReportGenerationStatus: ReportGenerationStatusShape | null
  affiliateReportGenerationTask: ReportGenerationTaskShape | null
  affiliateReportGenerationStatus: ReportGenerationStatusShape | null
  ticketSaleReportGenerationTask: ReportGenerationTaskShape | null
  ticketSaleReportGenerationStatus: ReportGenerationStatusShape | null
}

const initialState: ReportState = {
  webShopOrderReportLoading: 'idle',
  affiliateOrderReportLoading: 'idle',
  ticketSaleReportLoading: 'idle',
  singleWebShopOrderReport: null,
  singleAffiliateOrderReport: null,
  singleTicketSaleReport: null,
  websocketConnection: WebsocketConnectionStatus.IDLE,
  disconnectByError: false,
  webShopReportGenerationTask: null,
  webShopReportGenerationStatus: null,
  affiliateReportGenerationTask: null,
  affiliateReportGenerationStatus: null,
  ticketSaleReportGenerationTask: null,
  ticketSaleReportGenerationStatus: null
}

type BuiltAction<T> = {
  type: string
  meta: {
    timestamp: Date
  }
  error?: boolean
  payload?: {
    event: Event
    message: T
  }
}

type WebsocketMessage = BuiltAction<ReportGenerationStatusShape>

interface ReportGenerationTaskShape {
  task_id: string
}

interface ReportGenerationTaskProgressShape {
  current: number
  total: number
  report_name: string | null
}

export enum ReportGenerationStatus {
  PENDING = 'PENDING',
  PROGRESS = 'PROGRESS',
  SUCCESS = 'SUCCESS'
}

export interface ReportGenerationStatusShape {
  info: ReportGenerationTaskProgressShape | null
  status: ReportGenerationStatus
}

export const WEB_SHOP_ORDER_REPORT_PREFIX = 'WEB_SHOP_ORDER_REPORT'

export const resetWebShopOrderReportWebsocket = createAction('webShopOrderReport/reset/websocket')

interface FetchWebShopOrderReportRequest {
  report_name: string
}

const fetchWebShopOrderReport = createAsyncThunkWithErrorHandling<
  ReportResponse,
  FetchWebShopOrderReportRequest
>('report/fetchWebShopOrderReport', async data => {
  const { report_name } = data
  const response = await getAxiosInstance().get<ReportResponse>(
    `/api/v1/reports/web-orders/sales/${report_name}/`
  )
  return response.data
})

export interface WebShopOrderReportParamShape {
  authorities: string[]
  from_date?: string
  to_date?: string
}

const createWebShopOrderReport = createAsyncThunkWithErrorHandling<
  ReportGenerationTaskShape,
  WebShopOrderReportParamShape
>('report/createWebShopOrderReport', async data => {
  const response = await getAxiosInstance().post<ReportGenerationTaskShape>(
    `/api/v1/reports/web-orders/sales/`,
    data
  )
  return response.data
})

/** Affiliate **/

export const resetAffiliateOrderReportWebsocket = createAction(
  'affiliateOrderReport/reset/websocket'
)
export const AFFILIATE_ORDER_REPORT_PREFIX = 'AFFILIATE_ORDER_REPORT'

interface FetchAffiliateOrderReportRequest {
  report_name: string
}

export interface ReportResponse {
  download_url: string
}

const fetchAffiliateOrderReport = createAsyncThunkWithErrorHandling<
  ReportResponse,
  FetchAffiliateOrderReportRequest
>('report/fetchAffiliateOrderReport', async data => {
  const { report_name } = data
  const response = await getAxiosInstance().get<ReportResponse>(
    `/api/v1/reports/affiliate-orders/sales/${report_name}/`
  )
  return response.data
})

export interface AffiliateOrderReportParamShape {
  operators: string[]
  customer_group?: string
  payment_method?: string
  product_id?: string
  status?: string
  from_date?: string
  to_date?: string
}

const createAffiliateOrderReport = createAsyncThunkWithErrorHandling<
  ReportGenerationTaskShape,
  AffiliateOrderReportParamShape
>('report/createAffiliateOrderReport', async data => {
  const response = await getAxiosInstance().post<ReportGenerationTaskShape>(
    `/api/v1/reports/affiliate-orders/sales/`,
    data
  )
  return response.data
})

/** Ticket Sale **/

export const resetTicketSaleReportWebsocket = createAction('ticketSaleReport/reset/websocket')
export const TICKET_SALE_REPORT_PREFIX = 'TICKET_SALE_REPORT'

interface FetchTicketSaleReportRequest {
  report_name: string
}

const fetchTicketSaleReport = createAsyncThunkWithErrorHandling<
  ReportResponse,
  FetchTicketSaleReportRequest
>('report/fetchTicketSaleReport', async data => {
  const { report_name } = data
  const response = await getAxiosInstance().get<ReportResponse>(
    `/api/v1/reports/group-tickets/sales/${report_name}/`
  )
  return response.data
})

export interface TicketSaleReportParamShape {
  authorities: string[]
  targets: string[]
  from_date?: string
  to_date?: string
}

const createTicketSaleReport = createAsyncThunkWithErrorHandling<
  ReportGenerationTaskShape,
  TicketSaleReportParamShape
>('report/createTicketSaleReport', async data => {
  const response = await getAxiosInstance().post<ReportGenerationTaskShape>(
    `/api/v1/reports/group-tickets/sales/`,
    data
  )
  return response.data
})

const reportSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(fetchWebShopOrderReport.fulfilled, (state, { payload }) => {
        state.singleWebShopOrderReport = payload
        state.webShopOrderReportLoading = 'fulfilled'
      })
      .addCase(fetchWebShopOrderReport.pending, state => {
        state.singleWebShopOrderReport = initialState.singleWebShopOrderReport
        state.webShopOrderReportLoading = 'pending'
      })
      .addCase(fetchWebShopOrderReport.rejected, state => {
        state.webShopOrderReportLoading = 'rejected'
      })

      .addCase(createWebShopOrderReport.fulfilled, (state, { payload }) => {
        state.webShopReportGenerationTask = payload
        state.webShopOrderReportLoading = 'fulfilled'
      })
      .addCase(createWebShopOrderReport.pending, state => {
        state.webShopReportGenerationTask = initialState.webShopReportGenerationTask
        state.webShopOrderReportLoading = 'pending'
      })
      .addCase(createWebShopOrderReport.rejected, state => {
        state.webShopOrderReportLoading = 'rejected'
      })

      .addCase(`${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_CONNECT}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTING
      })
      .addCase(`${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_OPEN}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTED
      })
      .addCase(`${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_DISCONNECT}`, state => {
        Object.assign(state, initialState)
        state.websocketConnection = WebsocketConnectionStatus.CLOSING
      })
      .addCase(`${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_CLOSED}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CLOSED
      })
      .addCase(`${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_ERROR}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTION_ERROR
        state.disconnectByError = true
      })
      .addCase<string, WebsocketMessage>(
        `${WEB_SHOP_ORDER_REPORT_PREFIX}::${WEBSOCKET_MESSAGE}`,
        (state, data) => {
          const { payload } = data
          if (typeof payload !== 'undefined') {
            state.webShopReportGenerationStatus = payload.message
          }
        }
      )

      .addCase(resetWebShopOrderReportWebsocket, state => {
        state.websocketConnection = initialState.websocketConnection
        state.disconnectByError = initialState.disconnectByError
        state.webShopReportGenerationStatus = initialState.webShopReportGenerationStatus
        state.webShopReportGenerationTask = initialState.webShopReportGenerationTask
        state.singleWebShopOrderReport = initialState.singleWebShopOrderReport
      })

      /** Affiliate **/

      .addCase(fetchAffiliateOrderReport.fulfilled, (state, { payload }) => {
        state.singleAffiliateOrderReport = payload
        state.affiliateOrderReportLoading = 'fulfilled'
      })
      .addCase(fetchAffiliateOrderReport.pending, state => {
        state.singleAffiliateOrderReport = initialState.singleAffiliateOrderReport
        state.affiliateOrderReportLoading = 'pending'
      })
      .addCase(fetchAffiliateOrderReport.rejected, state => {
        state.affiliateOrderReportLoading = 'rejected'
      })

      .addCase(createAffiliateOrderReport.fulfilled, (state, { payload }) => {
        state.affiliateReportGenerationTask = payload
        state.affiliateOrderReportLoading = 'fulfilled'
      })
      .addCase(createAffiliateOrderReport.pending, state => {
        state.affiliateReportGenerationTask = initialState.affiliateReportGenerationTask
        state.affiliateOrderReportLoading = 'pending'
      })
      .addCase(createAffiliateOrderReport.rejected, state => {
        state.affiliateOrderReportLoading = 'rejected'
      })

      .addCase(`${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_CONNECT}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTING
      })
      .addCase(`${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_OPEN}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTED
      })
      .addCase(`${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_DISCONNECT}`, state => {
        Object.assign(state, initialState)
        state.websocketConnection = WebsocketConnectionStatus.CLOSING
      })
      .addCase(`${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_CLOSED}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CLOSED
      })
      .addCase(`${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_ERROR}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTION_ERROR
        state.disconnectByError = true
      })
      .addCase<string, WebsocketMessage>(
        `${AFFILIATE_ORDER_REPORT_PREFIX}::${WEBSOCKET_MESSAGE}`,
        (state, data) => {
          const { payload } = data
          if (typeof payload !== 'undefined') {
            state.affiliateReportGenerationStatus = payload.message
          }
        }
      )
      .addCase(resetAffiliateOrderReportWebsocket, state => {
        state.websocketConnection = initialState.websocketConnection
        state.disconnectByError = initialState.disconnectByError
        state.affiliateReportGenerationStatus = initialState.affiliateReportGenerationStatus
        state.affiliateReportGenerationTask = initialState.affiliateReportGenerationTask
        state.singleAffiliateOrderReport = initialState.singleAffiliateOrderReport
      })

      /** Ticket Sale **/

      .addCase(fetchTicketSaleReport.fulfilled, (state, { payload }) => {
        state.singleTicketSaleReport = payload
        state.ticketSaleReportLoading = 'fulfilled'
      })
      .addCase(fetchTicketSaleReport.pending, state => {
        state.singleTicketSaleReport = initialState.singleTicketSaleReport
        state.ticketSaleReportLoading = 'pending'
      })
      .addCase(fetchTicketSaleReport.rejected, state => {
        state.ticketSaleReportLoading = 'rejected'
      })
      .addCase(createTicketSaleReport.fulfilled, (state, { payload }) => {
        state.ticketSaleReportGenerationTask = payload
        state.ticketSaleReportLoading = 'fulfilled'
      })
      .addCase(createTicketSaleReport.pending, state => {
        state.ticketSaleReportGenerationTask = initialState.ticketSaleReportGenerationTask
        state.ticketSaleReportLoading = 'pending'
      })
      .addCase(createTicketSaleReport.rejected, state => {
        state.ticketSaleReportLoading = 'rejected'
      })

      .addCase(`${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_CONNECT}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTING
      })
      .addCase(`${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_OPEN}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTED
      })
      .addCase(`${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_DISCONNECT}`, state => {
        Object.assign(state, initialState)
        state.websocketConnection = WebsocketConnectionStatus.CLOSING
      })
      .addCase(`${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_CLOSED}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CLOSED
      })
      .addCase(`${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_ERROR}`, state => {
        state.websocketConnection = WebsocketConnectionStatus.CONNECTION_ERROR
        state.disconnectByError = true
      })
      .addCase<string, WebsocketMessage>(
        `${TICKET_SALE_REPORT_PREFIX}::${WEBSOCKET_MESSAGE}`,
        (state, data) => {
          const { payload } = data
          if (typeof payload !== 'undefined') {
            state.ticketSaleReportGenerationStatus = payload.message
          }
        }
      )
      .addCase(resetTicketSaleReportWebsocket, state => {
        state.websocketConnection = initialState.websocketConnection
        state.disconnectByError = initialState.disconnectByError
        state.ticketSaleReportGenerationStatus = initialState.ticketSaleReportGenerationStatus
        state.ticketSaleReportGenerationTask = initialState.ticketSaleReportGenerationTask
        state.singleTicketSaleReport = initialState.singleTicketSaleReport
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default reportSlice.reducer

export {
  createAffiliateOrderReport,
  createTicketSaleReport,
  createWebShopOrderReport,
  fetchAffiliateOrderReport,
  fetchTicketSaleReport,
  fetchWebShopOrderReport
}
